import {
  BACKGROUND_IMAGE_DPI,
  BACKGROUND_IMAGE_W,
  STROKE_COLOR_ACTIVE,
  STROKE_COLOR_GREEN,
  STROKE_COLOR_PASSIVE,
} from '@/web-components/import-wizard-v2/constants';
import {
  Category,
  ProductPrintPosition,
  RoundItemConfiguration,
  mm2px,
} from '@midocean/webapps-shared-domain';
import { getActivePanelNumber } from './drawPanelLines';
import { PanelTargetLine } from '@/typings/ProductPrintData';
import { AnnotationShape } from '@/web-components/import-wizard-v2/typings/common';
import { useStore as useProductStore } from '@/stores/v2/product';
import { lineWidthV2 } from '@/constants/areaLineWidth';

export function generatePanelSections(activePosition: ProductPrintPosition) {
  const productStore = useProductStore();
  const elements = [];

  if (activePosition.category === Category.RoundItem) {
    const categoryConfig =
      activePosition.categoryConfiguration as RoundItemConfiguration;

    const rectLineWidth = (lineWidthV2 * BACKGROUND_IMAGE_W) / 100;
    const panelWidth =
      mm2px(categoryConfig.productDiameter, BACKGROUND_IMAGE_DPI) +
      rectLineWidth;

    const scaledPanelWidth =
      (panelWidth * productStore.printPositionScaleFactor) /
      categoryConfig.numberOfPanels;

    for (let i = 0; i < categoryConfig.numberOfPanels; i++) {
      const centerX = scaledPanelWidth / 2 + i * scaledPanelWidth;
      elements.push(centerX);
    }

    return elements;
  } else {
    return [];
  }
}

export function drawPanelSections(
  activePosition: ProductPrintPosition,
  y: number,
  height: number,
  bufferx = 0
) {
  const productStore = useProductStore();
  if (activePosition.category === Category.RoundItem) {
    const originalImageWidth =
      (activePosition.originalImageWidthPx *
        activePosition.originalImageWebDpi) /
      activePosition.originalImageDpi;
    const categoryConfig =
      activePosition.categoryConfiguration as RoundItemConfiguration;
    const rectLineWidth = (lineWidthV2 * BACKGROUND_IMAGE_W) / 100;
    const panelWidth =
      mm2px(categoryConfig.productDiameter, BACKGROUND_IMAGE_DPI) +
      rectLineWidth;

    const elements = [];

    for (let index = 0; index < categoryConfig.numberOfPanels - 1; index++) {
      const basicPanelWidth =
        (panelWidth * productStore.printPositionScaleFactor) /
        categoryConfig.numberOfPanels;
      elements.push({
        strokeColor: STROKE_COLOR_GREEN,
        strokeWidth: 2,
        x1: basicPanelWidth * (index + 1) + bufferx,
        x2: basicPanelWidth * (index + 1) + bufferx,
        y1: y + height / 8,
        y2: y + height - height / 8,
      });
    }

    return elements;
  }
}

export function updateTargetLines(
  shape: AnnotationShape,
  centerCoordinatesOfPanels: PanelTargetLine[],
  panelsPositions: number[],
  isRemovingShape?: boolean
) {
  const productStore = useProductStore();
  const activePanelNumber = getActivePanelNumber(shape.x, panelsPositions);
  const centerOfPanelVerticalLine = centerCoordinatesOfPanels.find((item) => {
    if (
      Math.abs(
        Math.round(item.x1 + (item.x2 - item.x1) / 2) -
          Math.round(shape.x + shape.width / 2)
      ) < 10 &&
      activePanelNumber === item.panelNo &&
      item.isInteractive
    ) {
      return item;
    }
  });

  const centerOfPanelHorizontalLine = centerCoordinatesOfPanels.find((item) => {
    if (
      Math.abs(
        Math.round(item.y1 + (item.y2 - item.y1) / 2) -
          Math.round(shape.y + shape.height / 2)
      ) < 10 &&
      activePanelNumber === item.panelNo &&
      item.isInteractive &&
      item.id !== centerOfPanelVerticalLine?.id
    ) {
      return item;
    }
  });

  if (
    centerOfPanelVerticalLine &&
    centerOfPanelVerticalLine.isInteractive &&
    centerOfPanelHorizontalLine &&
    centerOfPanelHorizontalLine.isInteractive &&
    !isRemovingShape
  ) {
    const objToUpdateIndexX = centerCoordinatesOfPanels.findIndex(
      (item) => item.id === centerOfPanelVerticalLine.id
    );
    const objToUpdateIndexY = centerCoordinatesOfPanels.findIndex(
      (item) => item.id === centerOfPanelHorizontalLine.id
    );

    centerOfPanelVerticalLine.strokeColor = STROKE_COLOR_ACTIVE;
    centerOfPanelHorizontalLine.strokeColor = STROKE_COLOR_ACTIVE;

    centerCoordinatesOfPanels[objToUpdateIndexX] = {
      ...centerCoordinatesOfPanels[objToUpdateIndexX],
      strokeColor: STROKE_COLOR_ACTIVE,
    };
    centerCoordinatesOfPanels[objToUpdateIndexY] = {
      ...centerCoordinatesOfPanels[objToUpdateIndexY],
      strokeColor: STROKE_COLOR_ACTIVE,
    };

    productStore.setCenterCoordinatesOfPanels([...centerCoordinatesOfPanels]);
  } else {
    productStore.setCenterCoordinatesOfPanels([
      ...centerCoordinatesOfPanels.map((obj) => {
        obj.strokeColor = STROKE_COLOR_PASSIVE;
        return obj;
      }),
    ]);
  }

  return centerCoordinatesOfPanels;
}
